import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Grid, TextField, Typography } from "@mui/material";
import { isEqual } from "lodash-es";
import { useIntl } from "react-intl";

import AddIcon from "@mui/icons-material/Add";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import TitledCard from "../TitledCard";
import { useDevice } from "../../../../utils/DeviceProvider";
import { calcZones, useSmartalarm } from "./SmartalarmTemplate";
import { DisplayZoneValue, EntrydelayGroup } from "./types";
import { uuidv4 } from "../../../../utils/utils";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import SelectedZones from "./SelectedZones";
import ZoneChip from "../ZoneView";
import { behaviors, triggers } from "./SmartalarmBoard";

const EntrydelayList = () => {
  const [entrydelays, setEntrydelays] = useState<EntrydelayGroup[]>([]);
  const [zones, setZones] = useState<DisplayZoneValue[]>([]);

  const {
    device: { config },
  } = useDevice();
  const { value, saved, setValue } = useSmartalarm();

  const onEntrydelayChange = useCallback(
    (newValue: EntrydelayGroup[]) => {
      value.entrydelays = newValue;
      setEntrydelays([...newValue]);
      setValue(value);
    },
    [value, setValue]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setEntrydelays((old) => (isEqual(old, value.entrydelays) ? old : value.entrydelays));
  }, [value]);

  useEffect(() => {
    const newZones = calcZones(value.panels);
    setZones((old) => (isEqual(old, newZones) ? old : newZones));
  }, [value]);

  return <EntrydelayCard value={config ? entrydelays : null} saved={saved?.entrydelays || null} onChange={onEntrydelayChange} zones={zones} />;
};

interface EntrydelayCardProps {
  value: EntrydelayGroup[] | null;
  saved: EntrydelayGroup[] | null;
  zones: DisplayZoneValue[];
  onChange: (value: EntrydelayGroup[]) => void;
  disabled?: boolean;
}

const EntrydelayCard = ({ value, saved, zones, onChange, disabled }: EntrydelayCardProps) => {
  const [uuids, setUuids] = useState<string[]>([]);

  const { formatMessage } = useIntl();

  const addNewEntrydelay = useCallback(() => {
    if (!value) {
      return;
    }
    value.push({ uuid: uuidv4(), name: "", zones: [] });
    onChange(value);
  }, [value, onChange]);

  const onEntrydelayChange = useCallback(
    (index: number, v: EntrydelayGroup) => {
      if (!value) {
        return;
      }
      value[index] = v;
      onChange(value);
    },
    [value, onChange]
  );

  const onEntrydelayRemove = useCallback(
    (index: number) => {
      if (!value) {
        return;
      }
      value[index].deleted = true;
      onChange(value);
    },
    [value, onChange]
  );

  const onEntrydelayRestore = useCallback(
    (index: number) => {
      if (!value) {
        return;
      }
      delete value[index].deleted;
      onChange(value);
    },
    [value, onChange]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setUuids(value ? value.map(({ uuid }) => uuid) : []);
  }, [value]);

  return (
    <Grid container spacing={1}>
      {uuids?.length === value?.length &&
        uuids.map((uuid: string, index: number) => (
          <Grid item xs={12} key={`partition-${uuid}`}>
            <EntrydelayItem
              value={value[index]}
              saved={saved?.[index] || null}
              zones={zones.filter((z) => z.entrydelay)}
              notFollowerZones={zones.filter((z) => !z.entrydelay)}
              onChange={(v) => onEntrydelayChange(index, v)}
              onDelete={() => onEntrydelayRemove(index)}
              onRestore={() => onEntrydelayRestore(index)}
            />
          </Grid>
        ))}
      <Grid item>
        <Button startIcon={<AddIcon />} onClick={addNewEntrydelay} disabled={disabled}>
          {formatMessage({ defaultMessage: "New entry delay zone group" })}
        </Button>
      </Grid>
    </Grid>
  );
};

interface EntrydelayItemProps {
  value: EntrydelayGroup;
  saved: EntrydelayGroup | null;
  zones: DisplayZoneValue[];
  notFollowerZones: DisplayZoneValue[];
  onChange: (value: EntrydelayGroup) => void;
  onDelete: () => void;
  onRestore: () => void;
  disabled?: boolean;
}

const EntrydelayItem = ({ value, saved, zones, notFollowerZones, onChange, onDelete, onRestore, disabled }: EntrydelayItemProps) => {
  const [name, setName] = useState("");
  const [selection, setSelection] = useState<boolean[]>([]);

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();

  useEffect(() => {
    setName(value.name);
    setSelection((old) => {
      const newValue = zones.map(({ uuid }) => value.zones.includes(uuid));
      return isEqual(old, newValue) ? old : newValue;
    });
  }, [value, zones]);

  const notAvailableForEntrydelay = useMemo(() => {
    return notFollowerZones.filter(({ uuid }) => value.zones.includes(uuid));
  }, [notFollowerZones, value]);

  return (
    <TitledCard
      color={value.deleted ? editedBackground : undefined}
      contrastColor={value.deleted ? editedText : undefined}
      title={formatMessage({ defaultMessage: "Entry delay zone group{name, select, null {} other {: {name}}}" }, { name: name || null })}
    >
      <Grid container spacing={1} sx={{ padding: 1 }}>
        <Grid item>
          <TextField
            label={formatMessage({ defaultMessage: "Name" })}
            variant="filled"
            value={name}
            className={saved?.name !== name ? editedStyle.input : ""}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              value.name = name;
              onChange(value);
            }}
            sx={{ margin: 0 }}
            size="small"
            autoComplete="off"
            disabled={disabled || value.deleted}
          />
        </Grid>
        {!value.deleted && !disabled && (
          <Grid item container flex={1} flexDirection="row" justifyContent="flex-end">
            <Grid item>
              <Button startIcon={<DeleteForeverOutlinedIcon />} color="error" onClick={onDelete} sx={{ alignSelf: "flex-end" }}>
                {formatMessage({ defaultMessage: "Remove" })}
              </Button>
            </Grid>
          </Grid>
        )}
        {value.deleted && !disabled && (
          <Grid item sx={{ flex: 1, display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
            <Button color="inherit" onClick={onRestore} sx={{ alignSelf: "flex-start" }}>
              {formatMessage({ defaultMessage: "Restore" })}
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography component="span" variant="subtitle2">
            {formatMessage({ defaultMessage: "Zones in this entry delay:" })}
          </Typography>
          <SelectedZones
            label={formatMessage({ defaultMessage: "Choose zone" })}
            items={zones}
            value={selection}
            saved={zones.map(({ uuid }) => saved?.zones.includes(uuid) || false)}
            onChange={(v: boolean, index: number) => {
              selection[index] = v;
              value.zones = zones.filter((_, i) => selection[i]).map(({ uuid }) => uuid);
              onChange(value);
            }}
            onAllChange={(v: boolean[]) => {
              selection.forEach((_, i) => (selection[i] = v[i]));
              value.zones = zones.filter((_, i) => selection[i]).map(({ uuid }) => uuid);
              onChange(value);
            }}
            disabled={disabled || value.deleted}
          />
        </Grid>
        {notAvailableForEntrydelay.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography component="span" variant="subtitle2">
                {formatMessage({ defaultMessage: "Zones that are no longer available for entry delay:" })}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ ml: 2 }} container>
              {notAvailableForEntrydelay.map((zone) => {
                const { uuid, behavior, trigger } = zone;
                const bh = behaviors.find((b) => b.value === behavior.toString(2));
                const tr = triggers.find((t) => t.value === trigger);
                return (
                  <Grid item key={`no-longer-entrydelay-zone-${uuid}`}>
                    <ZoneChip zoneColor={bh?.color || tr?.color} icon={bh?.icon || tr?.icon} zone={zone} edited />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Grid>
    </TitledCard>
  );
};

export default EntrydelayList;
